/**
 * P1 — N1 · Competência 6 (Estatística) · task-model H3.30.05
 *
 *   "Calcular a média de um conjunto de dados apresentado num gráfico de colunas, em que
 *    cada coluna diz quantas vezes um valor aparece."
 *
 * REDESENHADO NA v5, a pedido do autor (2026-09-29). O item era de dados agrupados em
 * CLASSES, num histograma, e pedia ao aluno que "considerasse que cada entregador
 * percorreu a distância do ponto médio da sua classe" — uma hipótese forçada, que o
 * enunciado tinha de impor e o aluno, aceitar. Agora cada coluna é um VALOR: a primeira
 * é de quem percorreu 20 km, a segunda de quem percorreu 40 km, e assim por diante. A
 * média é a mesma conta ponderada, sem a ficção do representante de classe.
 *
 * Os distratores continuam sendo solucionadores defeituosos do mesmo problema: a média
 * simples dos valores (sem ponderar), a mediana, a moda, as frequências somadas ao
 * numerador, o corte das casas decimais e a divisão pelo número de colunas.
 */

import { formatBR, roundTo } from '../../core/num.ts';
import { partitionInt, sampleWhere } from '../../core/sampling.ts';
import type { Rng } from '../../core/rng.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { barras } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  ARRED_CASA_ERRADA,
  ARRED_TRUNCA,
  EST_DIVIDE_POR_CLASSES,
  EST_MEDIA_DAS_ALTURAS,
  EST_MEDIA_POR_MEDIANA,
  EST_MODA_POR_MEDIA,
  EST_SEM_PONDERAR,
  EST_SOMA_FREQUENCIAS,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

// ---------------------------------------------------------------------------
// Contextos
// ---------------------------------------------------------------------------

interface Contexto {
  /**
   * A frase que abre o texto-base: o que o gráfico mostra, e de quem. `{valores}` vira
   * a lista dos valores DESTA variante ("2, 4, 6 ou 8"): com quatro colunas, uma lista
   * fixa de cinco citaria um valor que o gráfico não tem.
   */
  readonly abertura: string;
  /** O comando: de que é a média. */
  readonly pergunta: string;
  readonly titulo: string;
  readonly eixoX: string;
  /** Quem é contado em cada coluna, no plural: "entregadores". */
  readonly eixoY: string;
  /** A unidade das alternativas; vazia quando o valor não tem unidade (notas). */
  readonly unidade: string;
  /** Os valores das colunas, em ordem; o item usa os quatro ou os cinco primeiros. */
  readonly valores: readonly number[];
}

const CONTEXTOS: readonly Contexto[] = [
  {
    // O exemplo do próprio autor: colunas de 20, 40, 60 e 80 km.
    abertura:
      'Os entregadores de um aplicativo fazem rotas de tamanhos fixos. O gráfico abaixo mostra quantos entregadores percorreram cada distância em um dia.',
    pergunta: 'Qual é a distância média percorrida pelos entregadores nesse dia?',
    titulo: 'Distância percorrida pelos entregadores',
    eixoX: 'Distância (km)',
    eixoY: 'Entregadores',
    unidade: 'km',
    valores: [20, 40, 60, 80, 100],
  },
  {
    abertura:
      'Os alunos de uma academia treinam em planos de {valores} horas por semana. O gráfico abaixo mostra quantos alunos há em cada plano.',
    pergunta: 'Qual é o tempo médio de treino semanal dos alunos da academia?',
    titulo: 'Horas de treino por semana',
    eixoX: 'Treino (h por semana)',
    eixoY: 'Alunos',
    unidade: 'horas',
    valores: [2, 4, 6, 8, 10],
  },
  {
    abertura:
      'Uma transportadora trabalha com caixas de massas padronizadas. O gráfico abaixo mostra quantas caixas de cada massa ela despachou em um dia.',
    pergunta: 'Qual é a massa média das caixas despachadas nesse dia?',
    titulo: 'Caixas despachadas, por massa',
    eixoX: 'Massa (kg)',
    eixoY: 'Caixas',
    unidade: 'kg',
    valores: [5, 10, 15, 20, 25],
  },
  {
    abertura:
      'Numa gincana, cada equipe fez {valores} pontos na prova final. O gráfico abaixo mostra quantas equipes fizeram cada pontuação.',
    pergunta: 'Qual é a pontuação média das equipes na prova final?',
    titulo: 'Pontos na prova final',
    eixoX: 'Pontos',
    eixoY: 'Equipes',
    unidade: 'pontos',
    valores: [10, 20, 30, 40, 50],
  },
  {
    abertura:
      'Um posto de saúde anota o tempo de espera de cada atendimento arredondado para {valores} minutos. O gráfico abaixo mostra quantos atendimentos houve com cada tempo de espera em um dia.',
    pergunta: 'Qual é o tempo médio de espera dos atendimentos nesse dia?',
    titulo: 'Tempo de espera dos atendimentos',
    eixoX: 'Espera (min)',
    eixoY: 'Atendimentos',
    unidade: 'minutos',
    valores: [5, 10, 15, 20, 25],
  },
];

// ---------------------------------------------------------------------------
// Parâmetros
// ---------------------------------------------------------------------------

export interface Params {
  readonly contexto: number;
  /** O valor de cada coluna, em ordem. */
  readonly valores: readonly number[];
  /** Quantos dados há em cada coluna. */
  readonly freqs: readonly number[];
  readonly total: number;
  /** Índice da coluna cuja altura foi apagada; -1 se nenhuma. */
  readonly freqOculta: number;
}

/** Maior altura de coluna: com o eixo de 2 em 2, o topo fica em 16. */
const FREQ_MAX = 16;

/**
 * Amostragem CONSTRUTIVA. O total sai de {20, 25, 50} — divisores que garantem média
 * exata em duas casas —, e as frequências são uma partição dele. A chave nunca é uma
 * dízima, e a taxa de rejeição por "número horroroso" é zero.
 */
function drawParams(rng: Rng): Params {
  const contexto = rng.int(0, CONTEXTOS.length - 1);
  const ctx = CONTEXTOS[contexto] as Contexto;
  const n = rng.pick([4, 4, 5]);
  const total = rng.pick([20, 25, 50]);
  const freqs = sampleWhere(
    rng.fork('freqs'),
    (g) => partitionInt(g, total, n, 2),
    (fs) => Math.max(...fs) <= FREQ_MAX,
    { label: 'freqs', maxTries: 400 },
  );
  // Variante difícil: a altura de uma coluna é apagada, e o total vem no enunciado.
  const freqOculta = rng.bool(0.2) ? rng.int(0, n - 1) : -1;
  return { contexto, valores: ctx.valores.slice(0, n), freqs, total, freqOculta };
}

const ctxDe = (p: Params): Contexto => CONTEXTOS[p.contexto] as Contexto;
const f = (p: Params, i: number): number => p.freqs[i] as number;
const numerador = (p: Params): number => p.valores.reduce((s, v, i) => s + v * f(p, i), 0);

/** A chave: soma(valor × quantidade) ÷ total. */
const solveKey = (p: Params): number => roundTo(numerador(p) / p.total, 2);

/** Os dados em fila, do menor ao maior — é daí que saem mediana e posição. */
function emFila(p: Params): number[] {
  const fila: number[] = [];
  p.valores.forEach((v, i) => {
    for (let k = 0; k < f(p, i); k++) fila.push(v);
  });
  return fila;
}

// ---------------------------------------------------------------------------
// Solucionadores
// ---------------------------------------------------------------------------

const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    // O erro que o item existe para pegar: a média dos valores das colunas, como se
    // cada coluna tivesse um dado só.
    error: EST_SEM_PONDERAR,
    priority: 1,
    solve: (p) => roundTo(p.valores.reduce((s, v) => s + v, 0) / p.valores.length, 2),
  },

  // --- faixa 2: sorteados entre si -----------------------------------------
  {
    // A mediana: o dado do meio da fila (ou a média dos dois do meio).
    error: EST_MEDIA_POR_MEDIANA,
    priority: 2,
    solve: (p) => {
      const fila = emFila(p);
      const m = fila.length / 2;
      const med =
        fila.length % 2 === 1
          ? (fila[Math.floor(m)] as number)
          : ((fila[m - 1] as number) + (fila[m] as number)) / 2;
      return roundTo(med, 2);
    },
  },
  {
    // A moda: o valor da coluna mais alta. Sem coluna mais alta única, não há moda para
    // confundir — e fabricar o distrator seria mentir.
    error: EST_MODA_POR_MEDIA,
    priority: 2,
    solve: (p) => {
      const maior = Math.max(...p.freqs);
      const onde = p.freqs.flatMap((q, i) => (q === maior ? [i] : []));
      return onde.length === 1 ? (p.valores[onde[0] as number] as number) : NOT_APPLICABLE;
    },
  },
  {
    // Soma as quantidades ao numerador, em vez de usá-las só como pesos: dá a chave + 1.
    error: EST_SOMA_FREQUENCIAS,
    priority: 2,
    solve: (p) => roundTo((numerador(p) + p.total) / p.total, 2),
  },
  {
    // Trunca em vez de arredondar; só existe quando a média não é inteira.
    error: ARRED_TRUNCA,
    priority: 2,
    solve: (p) => {
      const k = solveKey(p);
      const t = Math.trunc(k);
      return Math.abs(k - t) < 1e-9 ? NOT_APPLICABLE : t;
    },
  },

  {
    // Arredonda para inteiro, quando a média tem casas: "7,8" vira 8.
    error: ARRED_CASA_ERRADA,
    priority: 2,
    solve: (p) => {
      const k = solveKey(p);
      return Number.isInteger(k) ? NOT_APPLICABLE : Math.round(k);
    },
  },

  // --- faixa 3: reposição ---------------------------------------------------
  {
    // Lê o gráfico pelo eixo errado: a média das alturas das colunas, com a unidade dos
    // valores. Longe da chave, e por isso de reposição.
    error: EST_MEDIA_DAS_ALTURAS,
    priority: 3,
    solve: (p) => roundTo(p.total / p.valores.length, 2),
  },
  {
    // Divide pelo número de colunas. Fica muito acima da chave — implausível o bastante
    // para ser descartado por senso numérico —, e por isso só entra de reposição.
    error: EST_DIVIDE_POR_CLASSES,
    priority: 3,
    solve: (p) => roundTo(numerador(p) / p.valores.length, 2),
  },
];

// ---------------------------------------------------------------------------
// Explicações: a conta de cada erro, com os números desta variante
// ---------------------------------------------------------------------------

/** "20 × 8 + 40 × 4 + …". */
const somaProdutos = (p: Params): string =>
  p.valores.map((v, i) => `${formatBR(v)} × ${f(p, i)}`).join(' + ');

/** Casas das alternativas: nenhuma quando todas são inteiras; duas, senão. */
const casasDe = (valores: readonly number[]): number =>
  valores.every((v) => Number.isInteger(v)) ? 0 : 2;

/** "50 km", "7,4" (notas, sem unidade). */
const comUnidade = (p: Params, v: number, casas: number): string =>
  ctxDe(p).unidade === '' ? formatBR(v, casas) : `${formatBR(v, casas)} ${ctxDe(p).unidade}`;

function explicacoes(casas: number): Explicacoes<Params, number> {
  const valorTxt = (p: Params, v: number): string => comUnidade(p, v, casas);
  const quem = (p: Params): string => ctxDe(p).eixoY.toLowerCase();
  return {
    [EST_SEM_PONDERAR]: (p, v) => {
      const iMin = p.freqs.indexOf(Math.min(...p.freqs));
      const iMax = p.freqs.indexOf(Math.max(...p.freqs));
      return [
        txt(
          `Você fez a média simples dos valores das colunas: ` +
            `(${p.valores.map((x) => formatBR(x)).join(' + ')}) ÷ ${p.valores.length} = ${valorTxt(p, v)}. `,
        ),
        txt(
          `Assim, a coluna de ${comUnidade(p, p.valores[iMin] as number, 0)}, com ${f(p, iMin)} ${quem(p)}, pesa o mesmo que a de ` +
            `${comUnidade(p, p.valores[iMax] as number, 0)}, com ${f(p, iMax)}. Cada valor precisa ser multiplicado pela altura da sua ` +
            `coluna — o número de ${quem(p)} com aquele valor — antes da divisão pelo total, ${p.total}.`,
        ),
      ];
    },
    [EST_MEDIA_POR_MEDIANA]: (p, v) => [
      txt(
        `Você respondeu ${valorTxt(p, v)}, o valor do meio quando os ${p.total} dados são postos em fila, do menor ao maior. `,
      ),
      txt(
        'Isso é a mediana, não a média. A média usa todos os dados: soma de cada valor vezes a altura da ' +
          `sua coluna, dividida pelo total — (${somaProdutos(p)}) ÷ ${p.total}.`,
      ),
    ],
    [EST_MODA_POR_MEDIA]: (p, v) => [
      txt(`Você respondeu ${valorTxt(p, v)}, o valor da coluna mais alta — o que mais se repete. `),
      txt(
        'Isso é a moda, não a média. A média leva em conta todas as colunas, cada uma com o seu peso: ' +
          `(${somaProdutos(p)}) ÷ ${p.total}.`,
      ),
    ],
    [EST_SOMA_FREQUENCIAS]: (p, v) => [
      txt(
        `Você somou as quantidades ao numerador, em vez de usá-las só como pesos: ` +
          `(${formatBR(numerador(p))} + ${p.total}) ÷ ${p.total} = ${valorTxt(p, v)}. `,
      ),
      txt(
        `O numerador certo é só a soma dos produtos valor × quantidade: ${somaProdutos(p)} = ${formatBR(numerador(p))}.`,
      ),
    ],
    [ARRED_TRUNCA]: (p, v) => [
      txt(
        `Você cortou as casas decimais em vez de arredondar: a conta dá ` +
          `${formatBR(numerador(p))} ÷ ${p.total} = ${valorTxt(p, solveKey(p))}, e não ${valorTxt(p, v)}.`,
      ),
    ],
    [ARRED_CASA_ERRADA]: (p, v) => [
      txt(
        `Você arredondou para um número inteiro: a conta dá ${formatBR(numerador(p))} ÷ ${p.total} = ` +
          `${valorTxt(p, solveKey(p))}, e não ${valorTxt(p, v)}. A média não precisa ser um dos valores do gráfico, nem um número inteiro.`,
      ),
    ],
    [EST_MEDIA_DAS_ALTURAS]: (p, v) => [
      txt(
        `Você fez a média das alturas das colunas, (${p.freqs.join(' + ')}) ÷ ${p.valores.length}, e respondeu ${valorTxt(p, v)}. `,
      ),
      txt(
        `Mas as alturas contam ${quem(p)}; o que se quer é a média dos valores que elas contam — cada valor vezes a altura da sua coluna, dividido pelo total: (${somaProdutos(p)}) ÷ ${p.total}.`,
      ),
    ],
    [EST_DIVIDE_POR_CLASSES]: (p, v) => [
      txt(
        `Você dividiu a soma dos produtos, ${formatBR(numerador(p))}, pelo número de colunas: ` +
          `${formatBR(numerador(p))} ÷ ${p.valores.length} = ${valorTxt(p, v)}. `,
      ),
      txt(`O divisor certo é o total de ${quem(p)}, ${p.total}.`),
    ],
  };
}

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

export const mediaAgrupada: ItemTemplate<Params, number> = {
  id: templateId('h3-30-05-media-agrupada'),
  // v3: linhas-guia para alturas fora da escala e vão antes da primeira coluna.
  // v4: eixo de 2 em 2 sem linhas-guia, frequências até 16, classe apagada só com "?",
  // e o comando diz de que é a média.
  // v5: colunas por VALOR, e não por classe — sem o "considere o ponto médio" (autor).
  version: 5,
  taskModel: taskModelCode('H3.30.05'),
  skills: [skillCode('H3.30')],
  axis: 'II',
  level: 'N1',
  title: 'Média a partir de um gráfico de colunas',
  responseModel: '3PL',

  difficultySources: ['steps.2', 'two-supports'],

  params: drawParams,
  solve: (p) => solveKey(p),
  buggy: comExplicacoes(BUGGY, explicacoes(2)),

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.freqOculta >= 0) fontes.push('inverse-problem', 'steps.3plus');
    if (p.valores.length >= 5) fontes.push('reading-load.high');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const ctx = ctxDe(p);
    const quem = ctx.eixoY.toLowerCase();
    const oculta = p.freqOculta >= 0;
    const rotulo = (v: number): string => comUnidade(p, v, 0);
    const nomes = p.valores.map((v) => formatBR(v));
    const abertura = ctx.abertura.replace(
      '{valores}',
      `${nomes.slice(0, -1).join(', ')} ou ${nomes[nomes.length - 1] as string}`,
    );

    // A descrição NÃO pode revelar a altura que o enunciado apagou: o `alt` é lido por
    // leitores de tela e vazaria a resposta.
    const descricao =
      `Gráfico de colunas "${ctx.titulo}", com ${ctx.eixoX.toLowerCase()} no eixo horizontal e o número de ${quem} no vertical, de 2 em 2. ` +
      p.valores
        .map((v, i) =>
          i === p.freqOculta ? `${rotulo(v)}, coluna marcada com “?”` : `${rotulo(v)}, ${f(p, i)}`,
        )
        .join('; ') +
      '.';

    const fig = barras({
      categorias: p.valores.map((v) => formatBR(v)),
      valores: p.freqs.map((q, i) => (i === p.freqOculta ? null : q)),
      yTop: Math.max(8, Math.ceil((Math.max(...p.freqs) + 1) / 2) * 2),
      yStep: 2,
      xLabel: ctx.eixoX,
      yLabel: ctx.eixoY,
      title: ctx.titulo,
      desc: descricao,
    });

    const stem: RichText = oculta
      ? [
          txt(`${abertura} `),
          txt('A coluna marcada com “?” teve sua altura apagada, mas sabe-se que o '),
          strong('total'),
          txt(` é de ${p.total} ${quem}.`),
        ]
      : [txt(abertura)];

    const casas = casasDe([key, ...distractors.map((d) => d.value)]);
    const ex = explicacoes(casas);
    const comConta = distractors.map((d) => {
      const e = ex[d.error as string];
      return e ? { ...d, explanation: e(p, d.value) } : d;
    });
    const interaction = assembleSelectOne<number>({
      structure: oculta ? 'E3' : 'E2',
      key,
      distractors: comConta,
      render: (v) => [txt(comUnidade(p, v, casas))],
      raw: (v) => v,
      rng,
    });

    const conhecidas = p.freqs.filter((_q, i) => i !== p.freqOculta);
    const apagada: RichText = oculta
      ? [
          txt(
            `A coluna apagada tem ${p.total} − (${conhecidas.join(' + ')}) = ${f(p, p.freqOculta)} ${quem}.`,
          ),
          br,
        ]
      : [];

    return {
      stem,
      comando: [txt(ctx.pergunta)],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'colunas',
          scene: fig,
          alt: descricao,
        },
      ],
      interaction,
      rationale: [
        strong('Resolução. '),
        txt(
          `Cada coluna diz o número de ${quem} com aquele valor. A média é a soma de cada valor vezes a altura da sua coluna, dividida pelo total.`,
        ),
        br,
        ...apagada,
        txt(`Soma = ${somaProdutos(p)} = ${formatBR(numerador(p))}`),
        br,
        txt(`Total = ${p.total}`),
        br,
        txt(`Média = ${formatBR(numerador(p))} ÷ ${p.total} = ${comUnidade(p, key, casas)}.`),
      ],
      declared: oculta ? 'dificil' : 'media',
    };
  },

  validate(_built, p) {
    // A média cai entre o menor e o maior valor; se não cair, o erro é do gerador.
    const k = solveKey(p);
    const lo = p.valores[0] as number;
    const hi = p.valores[p.valores.length - 1] as number;
    if (k <= lo || k >= hi) return `média ${k} fora do intervalo (${lo}, ${hi})`;
    return null;
  },
};
